import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useApiQuery } from "../../../../common/infrastructure/hooks/useApiQuery";
import { useApiMutation } from "../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../common/infrastructure/routes/endpointsMetadata";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { showCustomConfirmDialog } from "../../../../common/components/dialogs/AppConfirmDialog";
import { getErrorToast } from "../../../../common/infrastructure/utilities/toastUtility";
import {
  GOOGLE_MEET_CONNECT_PARAM,
  isGoogleMeetConnectReturn,
} from "../../../../common/infrastructure/utilities/googleMeetConnectUtility";
import { ProfileTab } from "../models/profileTab";

export interface IGoogleMeetStatus {
  isAvailable: boolean;
  isConnected: boolean;
  googleEmail: string | null;
  needsReconnect: boolean;
}

interface IGoogleMeetConnectUrl {
  url: string;
}

export type GoogleMeetViewState =
  | "loading"
  | "error"
  | "unavailable"
  | "notConnected"
  | "connected"
  | "needsReconnect";

// The tab of "Моят профил" this card lives on; Google sends the owner back to it.
const INTEGRATIONS_TAB: ProfileTab = "integrations";

// What the OAuth bridge adds to the return URL (plus `error` when Google refuses).
const RETURN_PARAMS = ["code", "state", "oauth_return", "error", GOOGLE_MEET_CONNECT_PARAM];

const getProcessedKey = (code: string) => `googleMeetConnect_${code}`;

const readFlag = (key: string): boolean => {
  try {
    return sessionStorage.getItem(key) !== null;
  } catch {
    return false;
  }
};

const writeFlag = (key: string): boolean => {
  try {
    sessionStorage.setItem(key, "true");
    return true;
  } catch {
    // Storage can be blocked; the ref guard still covers a StrictMode double run.
    return false;
  }
};

const clearFlag = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // Nothing to clear when storage is blocked.
  }
};

// Code the server sends when the Meet permission was not ticked on Google's consent screen.
const GOOGLE_MEET_SCOPE_NOT_GRANTED = "google-meet-scope-not-granted";
const SCOPE_RETRY_KEY = "googleMeetScopeRetry";

const getGoogleErrorMessage =(error: string) =>
  error === "access_denied"
    ? "Достъпът до Google акаунта беше отказан."
    : "Свързването с Google не беше успешно. Опитайте отново.";

export const useGoogleMeetSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // The return URL already handled by this mount. React StrictMode runs effects twice in dev, and
  // the navigate() below re-renders before the effect settles; the one-time Google code must be
  // posted exactly once.
  const handledSearchRef = useRef<string | null>(null);

  const {
    data: status,
    refetch: refetchStatus,
    isFetching: isFetchingStatus,
  } = useApiQuery<IGoogleMeetStatus>({
    queryKey: [endpointsMetadata.googleMeet.getStatus],
    endpoint: endpointsMetadata.googleMeet.getStatus,
    globalIsLoading: false,
    retry: false,
  });

  const {
    mutate: requestConnectUrl,
    data: connectUrlData,
    isLoading: isRequestingUrl,
    reset: resetConnectUrl,
  } = useApiMutation<IGoogleMeetConnectUrl>({
    onSuccess: (data) => {
      // Same tab, not a new window: Google brings the owner back to this page.
      if (data?.url) {
        window.location.href = data.url;
      }
    },
  });

  // Tracked here, not with the mutation's own isLoading: React StrictMode's mount/unmount/mount
  // detaches the mutation's observer, so isLoading never went back to false and the badge stayed
  // on "Зареждане..." until a refresh. The mutation's options callbacks still run.
  const [isFinishingConnect, setIsFinishingConnect] = useState(false);
  const { mutate: finishConnect } = useApiMutation({
    // The success toast (the server's text) comes from useApiMutation; errors are shown below so the
    // "Meet not ticked" case can go back to Google instead.
    onSuccess: () => {
      clearFlag(SCOPE_RETRY_KEY);
    },
    onError: (error) => {
      const data = axios.isAxiosError(error) ? error.response?.data : undefined;
      const message =
        typeof data === "string" ? data : typeof data?.message === "string" ? data.message : null;

      // When Google asks for all scopes at once (a first connect without an earlier Google login)
      // it shows the Meet permission as an unticked box; the second pass asks for Meet alone and
      // goes through. So the owner is sent back once on their own; a second miss is a real refusal
      // and gets the message. A disconnect no longer revokes the grant, so a reconnect skips this.
      // Only when the flag could be stored: with blocked storage a retry could never be told apart
      // from the second miss, and the owner would bounce to Google forever.
      if (
        data?.code === GOOGLE_MEET_SCOPE_NOT_GRANTED &&
        !readFlag(SCOPE_RETRY_KEY) &&
        writeFlag(SCOPE_RETRY_KEY)
      ) {
        handleConnect();
        return;
      }

      clearFlag(SCOPE_RETRY_KEY);
      getErrorToast(message ?? "Свързването с Google не успя. Опитайте отново.");
    },
    onSettled: () => {
      refetchStatus();
      setIsFinishingConnect(false);
    },
  });

  const { mutate: disconnect, isLoading: isDisconnecting } = useApiMutation({
    onSuccess: () => {
      refetchStatus();
    },
  });

  // Pressing Back on Google's consent page restores this page from the browser's back/forward
  // cache with its state intact, so the connect URL would still be set and the card would stay
  // "connecting". Clear it when the page is shown from that cache.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        resetConnectUrl();
      }
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [resetConnectUrl]);

  // Back from Google: post the one-time code once, then take it off the address bar.
  useEffect(() => {
    if (!isGoogleMeetConnectReturn(location.search)) {
      return;
    }
    if (handledSearchRef.current === location.search) {
      return;
    }
    handledSearchRef.current = location.search;

    const params = new URLSearchParams(location.search);
    const error = params.get("error");
    const code = params.get("code");
    const state = params.get("state");

    // Cleaned first: the code must not linger in the address bar or history while the request runs.
    const cleanUrl = () => {
      const next = new URLSearchParams(location.search);
      RETURN_PARAMS.forEach((key) => next.delete(key));
      const search = next.toString();
      navigate(`${location.pathname}${search ? `?${search}` : ""}`, { replace: true });
    };

    if (error) {
      getErrorToast(getGoogleErrorMessage(error));
      cleanUrl();
      return;
    }

    if (!code || !state) {
      cleanUrl();
      return;
    }

    // Survives a remount (tab switch, reload) that a ref does not: a code works only once.
    const processedKey = getProcessedKey(code);
    if (readFlag(processedKey)) {
      cleanUrl();
      return;
    }
    writeFlag(processedKey);

    cleanUrl();
    setIsFinishingConnect(true);
    finishConnect({
      endpoint: endpointsMetadata.googleMeet.connect,
      method: "post",
      payload: { code, state },
      disableErrorToast: true,
    });
  }, [location.search, location.pathname, navigate, finishConnect]);

  const handleConnect = () => {
    const returnUrl = `${routePaths.portalMyProfile}?${new URLSearchParams({
      tab: INTEGRATIONS_TAB,
      [GOOGLE_MEET_CONNECT_PARAM]: "1",
    }).toString()}`;

    requestConnectUrl({
      endpoint: endpointsMetadata.googleMeet.getConnectUrl,
      method: "post",
      payload: { sourceUrl: window.location.origin, returnUrl },
      disableSuccessToast: true,
    });
  };

  const handleDisconnect = () => {
    showCustomConfirmDialog(
      "Новите дистанционни срещи няма да получават Meet линк. Съществуващите линкове остават. Достъпът остава в Google акаунта ви, докато не го премахнете от myaccount.google.com/permissions.",
      () => {
        disconnect({
          endpoint: endpointsMetadata.googleMeet.disconnect,
          method: "delete",
        });
      },
      () => {},
      "warning",
      "Да, прекъсни",
      "Отказ",
    );
  };

  const isLoading = isFetchingStatus || isFinishingConnect;
  const viewState: GoogleMeetViewState = !status
    ? isLoading
      ? "loading"
      : "error"
    : !status.isAvailable
      ? "unavailable"
      : status.needsReconnect
        ? "needsReconnect"
        : status.isConnected
          ? "connected"
          : "notConnected";

  return {
    viewState,
    googleEmail: status?.googleEmail ?? null,
    canDisconnect: status?.isConnected ?? false,
    isLoading,
    // Sending the owner to Google, or already on the way there.
    isConnecting: isRequestingUrl || !!connectUrlData?.url,
    isDisconnecting,
    handleConnect,
    handleDisconnect,
    refetchStatus,
  };
};
